import api from "./axios";

export interface Campaign {
  campaignId: number;
  campaignName: string;
  campaignType: string;
  campaignStatus: string;
  campaignCategoryName?: string;
  orderEntry: number;
  listSalesEntry: boolean | number | string;
}

export interface CampaignsResponse {
  success: boolean;
  total: number;
  campaigns: Campaign[];
}

export interface CampaignProduct {
  campaignProductId: number;
  productId: number;
  productName: string;
  price: string;
  productQty: number;
  shippingPrice: string;
  offerId: number;
  offerName: string;
  offerLabel: string;
  [key: string]: unknown;
}

export interface ShippingProfile {
  shippingId: number;
  shippingName: string;
  shippingPrice: string;
  [key: string]: unknown;
}

export interface CampaignCoupon {
  couponCode: string;
  applyTo: string;
  discountPrice: string | null;
  discountPerc: string | null;
  isRecurring: number;
  campaignProductId: number | null;
}

export interface CampaignProductsResponse {
  success: boolean;
  campaignId: number;
  total: number;
  products: CampaignProduct[];
  shippingProfiles: ShippingProfile[];
}

export interface OrderImportPayload {
  campaignId: number;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber?: string;
  companyName?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  shipFirstName?: string;
  shipLastName?: string;
  shipAddress1?: string;
  shipAddress2?: string;
  shipCity?: string;
  shipState?: string;
  shipCountry?: string;
  shipPostalCode?: string;
  shippingId?: number;
  paySource: string;
  offers?: string;
  couponCode?: string;
  notes?: string;
  campaignName?: string;
  campaignCategoryName?: string;
  totalAmount?: number;
}

export interface OrderImportResponse {
  success: boolean;
  message: string;
  data?: {
    orderId: string;
    orderEntryId: number;
  };
}

export const getOrderEntryCampaigns = () =>
  api.get<CampaignsResponse>("/checkout-campaign/campaigns/order-entry");

// --- Admin: cached campaign list + manual sync ---

export interface CachedCampaignsResponse {
  success: boolean;
  total: number;
  campaigns: Campaign[];
  lastSyncedAt: string | null;
}

export interface SyncCampaignsResponse extends CachedCampaignsResponse {
  message: string;
}

export const getCheckoutCampaignsCache = () =>
  api.get<CachedCampaignsResponse>(
    "/checkout-campaign/campaigns/order-entry/cache"
  );

export const syncCheckoutCampaigns = () =>
  api.post<SyncCampaignsResponse>(
    "/checkout-campaign/campaigns/order-entry/sync"
  );

// --- Admin: per-user CheckoutChamp API credentials ---

export interface UserCheckoutCredential {
  userId: number;
  name: string;
  email: string;
  role: string;
  hasCredentials: boolean;
  loginId: string | null;
  updatedAt: string | null;
}

export interface UsersCheckoutCredentialsResponse {
  success: boolean;
  users: UserCheckoutCredential[];
}

export const getUsersCheckoutCredentials = () =>
  api.get<UsersCheckoutCredentialsResponse>("/checkout-campaign/credentials");

export interface SaveCheckoutCredentialsPayload {
  loginId: string;
  password?: string;
}

export interface SaveCheckoutCredentialsResponse {
  success: boolean;
  message: string;
}

export const saveUserCheckoutCredentials = (
  userId: number,
  payload: SaveCheckoutCredentialsPayload
) =>
  api.put<SaveCheckoutCredentialsResponse>(
    `/checkout-campaign/credentials/${userId}`,
    payload
  );

export const getCampaignProducts = (
  campaignId: number,
  config?: { signal?: AbortSignal }
) =>
  api.get<CampaignProductsResponse>(
    `/checkout-campaign/campaigns/${campaignId}/products`,
    config
  );

export interface CampaignCouponResponse {
  success: boolean;
  campaignId: number;
  coupon: CampaignCoupon;
}

/**
 * Validate one coupon code against a campaign. Responds 404 when the code
 * isn't valid for that campaign.
 */
export const lookupCampaignCoupon = (campaignId: number, couponCode: string) =>
  api.get<CampaignCouponResponse>(
    `/checkout-campaign/campaigns/${campaignId}/coupons/${encodeURIComponent(
      couponCode.trim()
    )}`
  );

export const importOrder = (payload: OrderImportPayload) =>
  api.post<OrderImportResponse>("/checkout-campaign/orders/import", payload);

// --- Order Entries List ---

export interface OrderEntryRecord {
  id: number;
  user_id: number;
  creator_name: string;
  campaign_id: number;
  campaign_name: string | null;
  checkout_order_id: string | null;
  order_status: string;
  first_name: string;
  last_name: string;
  email_address: string;
  phone_number: string | null;
  offers: string | null;
  coupon_code: string | null;
  /** "PREPAID" for invoice orders, "CREDITCARD" when charged at creation. */
  pay_source: string | null;
  total_amount: string | null;
  invoice_number: string | null;
  invoice_status: "sending" | "sent" | "failed" | null;
  notes: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface OrderEntriesFilters {
  user_id?: number;
  email?: string;
  order_status?: string;
  date_from?: string;
  date_to?: string;
  page: number;
  limit: number;
}

export interface OrderEntriesResponse {
  success: boolean;
  records: OrderEntryRecord[];
  total: number;
  page: number;
  totalPages: number;
  limit: number;
}

export interface OrderEntryCreator {
  id: number;
  name: string;
}

export interface CreatorsResponse {
  success: boolean;
  creators: OrderEntryCreator[];
}

export const getOrderEntries = (filters: OrderEntriesFilters) => {
  const params: Record<string, string> = {
    page: String(filters.page),
    limit: String(filters.limit),
  };
  if (filters.user_id) params.user_id = String(filters.user_id);
  if (filters.email) params.email = filters.email;
  if (filters.order_status) params.order_status = filters.order_status;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  return api.get<OrderEntriesResponse>("/checkout-campaign/order-entries", { params });
};

export const getOrderEntryCreators = () =>
  api.get<CreatorsResponse>("/checkout-campaign/order-entries/creators");

export interface OrderEntryUserStat {
  user_id: number | null;
  user_name: string;
  email: string | null;
  total_orders: number;
  total_amount: string;
  completed_orders: number;
  completed_amount: string;
}

export interface OrderEntryUserStatsResponse {
  success: boolean;
  stats: OrderEntryUserStat[];
}

export interface UserStatsFilters {
  date_from?: string;
  date_to?: string;
}

export const getOrderEntryUserStats = (filters: UserStatsFilters) => {
  const params: Record<string, string> = {};
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  return api.get<OrderEntryUserStatsResponse>(
    "/checkout-campaign/order-entries/user-stats",
    { params }
  );
};

export interface SendInvoiceResponse {
  success: boolean;
  message: string;
  data?: {
    invoiceNumber: string;
  };
}

export const sendInvoice = (orderEntryId: number) =>
  api.post<SendInvoiceResponse>(
    `/checkout-campaign/order-entries/${orderEntryId}/send-invoice`
  );

export interface CampaignCheckoutUrl {
  campaignId: number;
  checkoutUrl: string;
  updatedAt: string;
}

export interface CampaignCheckoutUrlsResponse {
  success: boolean;
  urls: CampaignCheckoutUrl[];
}

export const getCampaignCheckoutUrls = () =>
  api.get<CampaignCheckoutUrlsResponse>("/checkout-campaign/checkout-urls");

export interface SaveCampaignCheckoutUrlResponse {
  success: boolean;
  message: string;
}

export const saveCampaignCheckoutUrl = (campaignId: number, checkoutUrl: string) =>
  api.put<SaveCampaignCheckoutUrlResponse>(
    `/checkout-campaign/checkout-urls/${campaignId}`,
    { checkoutUrl }
  );

export const deleteCampaignCheckoutUrl = (campaignId: number) =>
  api.delete<{ success: boolean }>(
    `/checkout-campaign/checkout-urls/${campaignId}`
  );

// --- Admin: per-campaign product cache (Campaign Products tab) ---

export interface CampaignProductSyncStatus {
  campaignId: number;
  campaignName: string | null;
  productCount: number;
  syncedAt: string | null;
}

export interface CampaignProductSyncStatusResponse {
  success: boolean;
  campaigns: CampaignProductSyncStatus[];
}

export const getCampaignProductSyncStatus = () =>
  api.get<CampaignProductSyncStatusResponse>("/checkout-campaign/product-cache");

export interface CachedCampaignProductsResponse {
  success: boolean;
  campaignId: number;
  products: CampaignProduct[];
  shippingProfiles: ShippingProfile[];
  syncedAt: string | null;
}

export const getCachedCampaignProductsAdmin = (campaignId: number) =>
  api.get<CachedCampaignProductsResponse>(
    `/checkout-campaign/product-cache/${campaignId}`
  );

export interface StartSyncCampaignProductsResponse {
  success: boolean;
  message: string;
  status: "running" | "succeeded" | "failed";
}

// Starts a background sync and returns immediately — large campaigns can take
// minutes, longer than a reverse proxy/CDN would hold the connection open.
// Poll getCampaignProductSyncJobStatus() to see when it finishes.
export const syncCampaignProducts = (campaignId: number) =>
  api.post<StartSyncCampaignProductsResponse>(
    `/checkout-campaign/product-cache/${campaignId}/sync`
  );

export interface CampaignProductSyncJob {
  status: "running" | "succeeded" | "failed";
  startedAt: number;
  finishedAt: number | null;
  result: {
    productsCount: number;
    shippingProfilesCount: number;
    couponsCount: number;
  } | null;
  error: string | null;
}

export interface CampaignProductSyncJobResponse {
  success: boolean;
  job: CampaignProductSyncJob | null;
}

export const getCampaignProductSyncJobStatus = (campaignId: number) =>
  api.get<CampaignProductSyncJobResponse>(
    `/checkout-campaign/product-cache/${campaignId}/sync/status`
  );
